/* Shared Country / State / City selects — same geography API as Travel destination form. */
import React, { useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import {
  fetchAllCountries,
  fetchStatesByCountry,
  fetchCitiesByCountryAndState,
} from 'api/countriesNow';
import { useAuth } from 'auth/Auth';

const normalizeCountryOption = (option) => {
  if (!option) return null;
  if (typeof option === 'string') {
    const label = option.trim();
    return label ? { label, code: '' } : null;
  }
  const label = String(option.label || option.value || option.name || '').trim();
  if (!label) return null;
  return { label, code: option.code || option.cca2 || '' };
};

export function CountrySelect({
  value = '',
  onChange,
  label = 'Country',
  placeholder = 'Select country',
  required = false,
  error = false,
  helperText = '',
  disabled = false,
  fullWidth = true,
  margin = 'none',
  size = 'medium',
  inputHeight = '45px',
  includeHidden = false,
  axiosInstance: axiosOverride = null,
}) {
  const { axiosInstance: authAxios } = useAuth();
  const axiosInstance = axiosOverride || authAxios;
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const list = await fetchAllCountries(axiosInstance, { includeHidden });
        if (cancelled) return;
        setOptions(
          (list || [])
            .map(normalizeCountryOption)
            .filter(Boolean)
            .sort((a, b) => a.label.localeCompare(b.label))
        );
      } catch (err) {
        console.error('Error loading countries:', err);
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [axiosInstance, includeHidden]);

  const selected =
    options.find(
      (c) => c.label.toLowerCase() === String(value || '').trim().toLowerCase()
    ) ||
    (value ? { label: String(value), code: '' } : null);

  return (
    <Autocomplete
      options={options}
      loading={loading}
      disabled={disabled}
      value={selected}
      getOptionLabel={(option) => option?.label || ''}
      isOptionEqualToValue={(option, val) =>
        option?.label?.toLowerCase() === val?.label?.toLowerCase()
      }
      onChange={(_, newValue) => onChange?.(newValue?.label || '')}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth={fullWidth}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          variant="outlined"
          margin={margin}
          size={size}
          InputProps={{
            ...params.InputProps,
            style: { height: inputHeight, ...(params.InputProps?.style || {}) },
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export function StateSelect({
  country = '',
  value = '',
  onChange,
  label = 'State',
  placeholder = 'Select state',
  required = false,
  error = false,
  helperText = '',
  disabled = false,
  fullWidth = true,
  margin = 'none',
  size = 'medium',
  freeSolo = true,
  inputHeight = '45px',
  includeHidden = false,
  axiosInstance: axiosOverride = null,
}) {
  const { axiosInstance: authAxios } = useAuth();
  const axiosInstance = axiosOverride || authAxios;
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!country) {
        setOptions([]);
        return;
      }
      setLoading(true);
      try {
        const states = await fetchStatesByCountry(axiosInstance, country, { includeHidden });
        if (!cancelled) setOptions(states || []);
      } catch (err) {
        console.error('Error loading states:', err);
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [country, axiosInstance, includeHidden]);

  return (
    <Autocomplete
      freeSolo={freeSolo}
      options={options}
      loading={loading}
      disabled={disabled || !country}
      value={value || null}
      onChange={(_, newValue) => onChange?.(newValue || '')}
      onInputChange={(_, newInputValue, reason) => {
        // freeSolo allows typed values; strict mode only commits option picks.
        if (!freeSolo) return;
        if (reason === 'input' || reason === 'clear') {
          onChange?.(newInputValue || '');
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth={fullWidth}
          label={label}
          placeholder={!country ? 'Select country first' : placeholder}
          required={required}
          error={error}
          helperText={helperText}
          variant="outlined"
          margin={margin}
          size={size}
          InputProps={{
            ...params.InputProps,
            style: { height: inputHeight, ...(params.InputProps?.style || {}) },
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export function CitySelect({
  country = '',
  state = '',
  value = '',
  onChange,
  label = 'City',
  placeholder = 'Select city',
  required = false,
  error = false,
  helperText = '',
  disabled = false,
  fullWidth = true,
  margin = 'none',
  size = 'medium',
  freeSolo = true,
  inputHeight = '45px',
  axiosInstance: axiosOverride = null,
}) {
  const { axiosInstance: authAxios } = useAuth();
  const axiosInstance = axiosOverride || authAxios;
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!country) {
        setOptions([]);
        return;
      }
      setLoading(true);
      try {
        const cities = await fetchCitiesByCountryAndState(
          axiosInstance,
          country,
          state
        );
        if (!cancelled) setOptions(cities || []);
      } catch (err) {
        console.error('Error loading cities:', err);
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [country, state, axiosInstance]);

  return (
    <Autocomplete
      freeSolo={freeSolo}
      options={options}
      loading={loading}
      disabled={disabled || !country}
      value={value || null}
      onChange={(_, newValue) => onChange?.(newValue || '')}
      onInputChange={(_, newInputValue, reason) => {
        if (!freeSolo) return;
        if (reason === 'input' || reason === 'clear') {
          onChange?.(newInputValue || '');
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth={fullWidth}
          label={label}
          placeholder={!country ? 'Select country first' : placeholder}
          required={required}
          error={error}
          helperText={helperText}
          variant="outlined"
          margin={margin}
          size={size}
          InputProps={{
            ...params.InputProps,
            style: { height: inputHeight, ...(params.InputProps?.style || {}) },
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default CountrySelect;
